/**
 * **画面を切ってずらす**（Issue #553）の形。舞台の設定（`editorPorts/fxScene.js::splitOf`）が出した値を、
 * three の層の後処理（`three/post.js`）に渡す形にする。**three を import しない**（層の React 部品が読むので）。
 *
 * 切る線は点 `point`（舞台のピクセル）を通り、向き `angle`（度。y 下向き、0 = 水平で右向き）。
 * 線の法線 `normal` の向こう側（`dot(p - point, normal) > 0`）は線に沿って `+offset`・線から `gap / 2` 離れ、
 * 手前側は `-offset`・`gap / 2` 離れる。**盤・オブジェクト・演出の 3 つの層に同じ値を渡す**ので、層をまたいで同じ所で切れる。
 * HUD は three の層ではないので切れない（盤の外の表示として残る）。
 */

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/**
 * `splitOf` の値 ＋ 解いた点 → 後処理に渡す形 `{ point, tangent, normal, offset, gap }`。点が無ければ null。
 * @param split `{ nudge, angle, offset, gap }`
 * @param point 舞台のピクセル `[x, y]`（`at` を舞台が解いたもの）
 */
export function splitGeometry(split, point) {
  if (!split || !Array.isArray(point) || !point.every(Number.isFinite)) return null;
  const a = (num(split.angle) * Math.PI) / 180;
  const tangent = [Math.cos(a), Math.sin(a)];
  const normal = [-Math.sin(a), Math.cos(a)];
  const nudge = split.nudge ?? [0, 0];
  return Object.freeze({
    point: [point[0] + num(nudge[0]), point[1] + num(nudge[1])],
    tangent,
    normal,
    offset: num(split.offset),
    gap: Math.max(0, num(split.gap)),
  });
}

/**
 * **舞台のピクセル `p` に写るのは、元の絵のどこか**（後処理のシェーダと同じ式。テストと確認用）。
 * 切れ目（`gap`）に落ちたら null（下の層が見える）。
 */
export function splitSource(geo, p) {
  if (!geo) return p;
  const [px, py] = geo.point;
  const d = (p[0] - px) * geo.normal[0] + (p[1] - py) * geo.normal[1];
  const side = d >= 0 ? 1 : -1;
  const sx = p[0] - side * (geo.tangent[0] * geo.offset + geo.normal[0] * geo.gap / 2);
  const sy = p[1] - side * (geo.tangent[1] * geo.offset + geo.normal[1] * geo.gap / 2);
  const d2 = (sx - px) * geo.normal[0] + (sy - py) * geo.normal[1];
  return d2 * side < 0 ? null : [sx, sy];
}

/** 後処理に切れ目を足す（切れ目が無ければ `post` のまま）。`post` が null でも切れ目だけの後処理を作る */
export function withSplit(post, geo) {
  if (!geo) return post ?? null;
  return { bloom: 0, threshold: 0.6, radius: 1.4, chroma: 0, ...(post ?? {}), split: geo };
}
